import { UnitsFilters } from "@/constant"
import {
  GetPropertiesGetRequestParams,
  MultiUnitSummaryType,
  PropertyDataV2,
} from "commonModule/services/common"
import {
  ADMINISTRATIVE_AREA_LEVEL_1,
  LOCALITY,
} from "components/searchBar/types"
import { STATES_MAPPER } from "configs/country-state-names"
import { AddressComponent } from "stores/save-search/types"
import { BoundObjectTypes } from "utils/helper.type"

import { getFiltersInLocalStorage } from "./local-storage"

function getLocation(location: string | null) {
  if (!location) return ""
  const searchName = location?.split(",")?.reverse()
  const country = searchName[0]?.trim()

  const selectedStateID = STATES_MAPPER[
    searchName[1]?.trim().replace(/\d+/g, "")
  ] as string

  const selectedState =
    selectedStateID || searchName[1]?.trim().replace(/\d+/g, "")

  const city = searchName[2]?.trim()
  if (city) return city
  if (selectedState?.trim()) return selectedState?.trim()
  return country
}

// state & country
const getDescLocation = (location: string | null) => {
  if (!location) return ""
  const searchName = location?.split(",")?.reverse()
  const country =
    searchName[0]?.trim() == "USA" ? "United States" : searchName[0]?.trim()

  const selectedStateID = STATES_MAPPER[
    searchName[1]?.trim().replace(/\d+/g, "")
  ] as string

  const selectedState =
    selectedStateID || searchName[1]?.trim().replace(/\d+/g, "")

  const city = searchName[2]?.trim()
  if (city) return `${city}, ${selectedState?.trim()}`
  if (selectedState?.trim())
    return `${selectedState?.trim()}, ${country?.trim()}`
  return country
}

export function getTitle(
  searchName: string | null | undefined,
  isChangeTitle?: boolean,
) {
  if (!searchName) return ""
  const location = searchName.split(",")?.reverse()
  const isCountry = !location[1]

  let placeName = ""
  if (isCountry) {
    placeName = location[0]
  } else {
    placeName = getLocation(searchName)
  }

  return isChangeTitle
    ? `${placeName} Vacation Rentals | Book Now, Save Up to 30%`
    : `Best deals on vacation rentals in ${placeName} | Whimstay`
}

export const getDescription = (
  searchName: string | null | undefined,
  isChangeDescription?: boolean,
) => {
  if (!searchName) return ""
  const location = searchName?.split(",")?.reverse()
  const isCountry = !location[1]

  let placeName = ""
  if (isCountry) {
    placeName = location[0] == "USA" ? "United States" : location[0]
  } else {
    placeName = getDescLocation(searchName)
  }

  // "Get the best deals on last minute vacation rentals in {name of city}, {name of state}. Find affordable mountain cabins, beach houses, condos, and more."
  return isChangeDescription
    ? `${placeName} vacation rentals made affordable. Get up to 30% off houses, condos, apartments, and more with Whimstay's exclusive deals.`
    : `Get the best deals on premium vacation rentals in ${placeName}. Find affordable mountain cabins, beach houses, condos, and more.`
}

export const stringifyBounds = (bounds: BoundObjectTypes | null): string => {
  // [se.lat,se.lng,nw.lat,nw.lng,sw.lat,sw.lng,ne.lat,ne.lng]
  const arr: string[] = []
  if (bounds) {
    if (bounds.se && bounds.nw && bounds.sw && bounds.ne) {
      arr.push(`${bounds.se.lat}`)
      arr.push(`${bounds.se.lng}`)
      arr.push(`${bounds.nw.lat}`)
      arr.push(`${bounds.nw.lng}`)
      arr.push(`${bounds.sw.lat}`)
      arr.push(`${bounds.sw.lng}`)
      arr.push(`${bounds.ne.lat}`)
      arr.push(`${bounds.ne.lng}`)
    }
  }
  return arr.join(",")
}

// get month name
const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]
// get full month name
const monthNamesFull = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

export function getMonthName(monthIndex: number, displayMonthNameFull = false) {
  return displayMonthNameFull
    ? monthNamesFull[monthIndex]
    : monthNames[monthIndex] // get the month name from a month index
}

// Function to convert month name to its numeric representation
function getMonthIndex(monthName: string) {
  return monthNames.findIndex(
    (month) => month?.toLowerCase() === monthName?.toLowerCase(),
  )
}

// Function to parse the date string in the format "DD Month"
export function parseDateString(dateString: string) {
  const [monthName, day] = dateString ? dateString.split(" ") : []
  if (!day || !monthName) {
    return null // Invalid date format
  }
  const month = getMonthIndex(monthName)
  if (month === -1 || isNaN(parseInt(day, 10))) {
    return null // Invalid date format
  }
  return new Date(new Date().getFullYear(), month, parseInt(day, 10))
}

// Function to format a date range
export function formatDateRange(
  date1: string | null,
  date2: string | null,
  displayMonthNameFull = false,
) {
  const startDate = parseDateString(date1 || "")
  const endDate = parseDateString(date2 || "")
  if (startDate !== null && endDate !== null) {
    const startMonth = startDate.getMonth()
    const endMonth = endDate.getMonth() // end month is exclusive

    if (startMonth === endMonth) {
      // Same month
      return `${getMonthName(
        endMonth,
        displayMonthNameFull,
      )} ${startDate.getDate()} - ${endDate.getDate()}`
    } else {
      // different months case
      return `${getMonthName(
        startMonth,
        displayMonthNameFull,
      )} ${startDate.getDate()} - ${getMonthName(
        endMonth,
        displayMonthNameFull,
      )} ${endDate.getDate()}`
    }
  }
}

export function isDifferentMonth(d1: string | null, d2: string | null) {
  const start = parseDateString(d1 || "")
  const end = parseDateString(d2 || "")
  if (!start || !end) return false
  return start.getMonth() !== end.getMonth()
}

export const extractInt = (
  searchParams: URLSearchParams,
  key: string,
  fallback = "0",
) => {
  return parseInt(searchParams.get(key) || fallback, 10)
}

export const extractBoolean = (
  searchParams: URLSearchParams,
  key: string,
  fallback = "false",
) => {
  return (searchParams.get(key) || fallback) === "true"
}

export function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
  const forbiddenKeys = ["e", "E", "+", "-", "Minus"]

  if (forbiddenKeys.includes(e.key)) {
    e.preventDefault()
  }
}

// Generates a string describing the total number of guests and whether pets are allowed.
export const handleGuestValue = (params: {
  adultCount: number
  childCount: number
  petsAllowed: boolean
}): string => {
  const { adultCount, childCount, petsAllowed } = params
  const totalGuests = (adultCount || 0) + (childCount || 0) // Safeguard against undefined
  const result: string[] = []

  // Guest count logic
  if (totalGuests === 1) {
    result.push(`1 Guest`)
  } else if (totalGuests > 1) {
    result.push(`${totalGuests} guests`)
  }
  // Pets allowed logic
  if (petsAllowed) {
    result.push(`pets allowed`)
  }

  // Join and return the result as a string, or empty string if nothing matches
  return result.join(", ") || ""
}

// Utility function to process place description
export const processPlaceDescription = (
  description: string,
  sliceCount: number = 3,
) => {
  return description
    .split(",")
    .reverse()
    .slice(0, sliceCount)
    .map((term) => term.trim())
    .reverse()
    .join(", ")
}

// function to check if searched place is administrative_area_level_1 or locality
export const isPlaceTypeAreaLevel1OrLocality = (
  typesArray: string[] | null,
) => {
  // Check if the array contains "administrative_area_level_1" or "locality"
  if (typesArray) {
    return (
      typesArray.includes(ADMINISTRATIVE_AREA_LEVEL_1) ||
      typesArray.includes(LOCALITY)
    )
  }
  return false
}
export type ResultType = {
  city: string
  state: string
  country: string
}

export function getAddressComponents(address: AddressComponent[]): ResultType {
  const result: ResultType = {
    city: "NA",
    state: "NA",
    country: "NA",
  }

  // Function to find the first match of the given type
  const findComponent = (types: string[]): string | null => {
    for (const component of address) {
      if (types.some((type) => component.types.includes(type))) {
        return component.long_name
      }
    }
    return null
  }

  // Check for city in the prioritized order
  result.city =
    findComponent(["administrative_area_level_3"]) ||
    findComponent(["locality"]) ||
    findComponent(["sublocality"]) ||
    findComponent(["sublocality_level_1"]) ||
    "NA"

  // Check for state and country
  for (const component of address) {
    if (component.types.includes("administrative_area_level_1")) {
      result.state = component.long_name
    } else if (component.types.includes("country")) {
      result.country = component.long_name
    }
  }

  return result
}

// Function to generate a list of bedroom filter options as strings like "1 Bed (2), 2 Beds (1), 3+ Beds (0)"
export const generateMultiUnitsFilterOptions = (
  multiUnitSummary: MultiUnitSummaryType | null, // Data summary of available bedroom types
  count: number, // Total number of units
): string[] => {
  const options: string[] = [] // Initialize the result array to store filter option labels

  // If there's no summary data, return only the "All" option with the total count
  if (!multiUnitSummary) {
    return [`${UnitsFilters.ALL} (${count})`] // e.g., "All (5)"
  }

  // Always include the "All" option at the top with total count
  options.push(`${UnitsFilters.ALL} (${count})`)

  const bedroomFilters = multiUnitSummary.bedroomFilters || {} // Get bedroom filter object or use an empty object
  // Check if there are any bedrooms with more than 3 beds — needed to decide label "3 Beds" vs "3+ Beds"
  const hasMoreThanThreeBeds = Object.keys(bedroomFilters).some(
    (key) => parseInt(key) > 3,
  )
  let threePlusBedCount = 0
  // Loop through each key-value pair in bedroomFilters
  Object.entries(bedroomFilters).forEach(([key, value]) => {
    const numKey = parseInt(key) // Convert the key string to a number

    if (numKey === 0) {
      // If the key is 0, it means it's a Studio
      options.push(`${UnitsFilters.STUDIO} (${value})`) // e.g., "Studio (3)"
    } else if (numKey >= 3) {
      threePlusBedCount += value // Sum all values where key >= 3
    } else {
      // For 1 or 2 bedrooms, format label as "1 Bed" or "2 Beds"
      const label = `${numKey} ${numKey > 1 ? "Beds" : "Bed"}`
      options.push(`${label} (${value})`)
    }
  })

  // Add the "3+ Beds" label if there are any units with 3 or more beds
  if (threePlusBedCount > 0) {
    // For 3 or more bedrooms, only add one label — either "3 Beds" or "3+ Beds"
    const label = hasMoreThanThreeBeds
      ? UnitsFilters.THREE_PLUS_BED // e.g., "3+ Beds"
      : UnitsFilters.THREE_BED // e.g., "3 Beds"
    options.push(`${label} (${threePlusBedCount})`)
  }

  return options // Return the final list of filter labels
}

export const generateCustomUUID = () => {
  return Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 16).toString(16),
  ).join("")
}

export const getFiltersForMixpanelEvent = (
  search: GetPropertiesGetRequestParams | null,
): string[] => {
  const fullFilterList =
    (JSON.parse(getFiltersInLocalStorage()) as {
      featureCategory: string
      id: number
      keyName: string
    }[]) || []

  const selectedFilters =
    [
      ...(search?.FeaturesAndAmenities?.split(",") || []),
      ...(search?.Location?.split(",") || []),
    ]
      .map(
        (id) =>
          fullFilterList?.find((filterItem) => filterItem.id === +id)
            ?.keyName as string,
      )
      .filter((x) => !!x) || []

  if (search?.petAllowed) {
    selectedFilters?.push("Pets allowed")
  }
  if (search?.partyAllowed || search?.eventAllowed) {
    selectedFilters?.push("Events allowed")
  }
  if (
    search?.childAllowed ||
    (search?.childrenCount && +search.childrenCount > 0)
  ) {
    selectedFilters?.push("Children welcome")
  }
  if (search?.smokeAllowed) {
    selectedFilters?.push("Smoking allowed")
  }
  if (search?.cancellable) {
    selectedFilters?.push("Cancellation allowed")
  }
  return selectedFilters
}

// function that returns saved price
export function getSavedPrice({
  shouldDisplayTotalPrice,
  searchQuery,
  property,
  rate,
  formatter,
  addCurrencySymbol = true,
}: {
  shouldDisplayTotalPrice: boolean
  searchQuery: GetPropertiesGetRequestParams | null
  property: PropertyDataV2
  rate: number
  formatter: (
    minimumFractionDigits?: number,
    maximumFractionDigits?: number,
  ) => Intl.NumberFormat
  addCurrencySymbol?: boolean
}) {
  let savePrice = ""

  if (shouldDisplayTotalPrice) {
    if (searchQuery?.sortBy === "bestDeals") {
      savePrice = `${property?.totalSavedPercent || 0}%`
    } else {
      const value =
        (property?.otaName && property?.whimstaySaveAmount
          ? property?.whimstaySaveAmount
          : property.discount) * rate
      savePrice = addCurrencySymbol
        ? formatter(0, 0).format(value)
        : value.toFixed(0)
    }
  } else {
    if (searchQuery?.sortBy === "bestDeals") {
      savePrice = `${property?.perNightSavedPercent || 0}%`
    } else {
      const value =
        Math.round(property.maxPrice * rate) -
        Math.round(property.minPrice * rate)
      savePrice = addCurrencySymbol
        ? formatter(0, 0).format(value)
        : value.toFixed(0)
    }
  }

  return savePrice
}
